import { logger } from '@adornis/base/logging.js';
import { l } from '@adornis/base/rxjs-extension/rxjs-helpers.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import { Size } from '@adornis/buildify/client/globals/enums.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { RenderableConfigFormController } from '@adornis/buildify/client/RenderableConfigFormController.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import '@adornis/digitale-helden-shared/client/components/component-carousel';
import '@adornis/digitale-helden-shared/client/theme/d-card.js';
import '@adornis/digitale-helden-shared/client/theme/d-checkbox';
import '@adornis/digitale-helden-shared/client/theme/d-dropdown-selection';
import '@adornis/digitale-helden-shared/client/theme/d-icon';
import '@adornis/digitale-helden-shared/client/theme/d-input.js';
import { consentCookieValueSubject } from '@adornis/digitale-helden-shared/tracking/client/consent-tool.js';
import { CookieFactory } from '@adornis/digitale-helden-shared/tracking/client/types/CookieFactory.js';
import '@adornis/file-utils/client/x-image';
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { distinctUntilChanged, map, switchMap } from 'rxjs';
import '../../client/theme/d-text.js';
import {
  BaseInstagramFeed,
  InstagramFeedDisplayMode,
  InstagramGalleryPagination,
  type InstagramPost,
} from '../db/BaseInstagramFeed.js';

@customElement('marketing-instagram-feed-editor')
export class MInstagramFeedEditor extends ChemistryLitElement {
  @property() contentController!: RenderableConfigFormController<BaseInstagramFeed>;
  @property() host!: ChemistryLitElement;
  @property() controllerBaseKeyPath!: string[];
  @property() content!: BaseInstagramFeed;
  @property() size!: Size;

  @state() private readonly _cachedPosts = new RXController(this, [] as InstagramPost[], l('_fullGallery'));
  @state() private _nextPagingUrl?: string;

  protected applyMorePosts(nextResponse: InstagramGalleryPagination) {
    this._cachedPosts.next(this._cachedPosts.value.concat(nextResponse.response));
    this._nextPagingUrl = nextResponse.next;
  }

  protected async loadMorePosts() {
    if (!this._nextPagingUrl) return;

    const nextPostsPagination = await InstagramGalleryPagination.fetchInstagramGalleryPaginated(this._nextPagingUrl)(
      InstagramGalleryPagination.defaultFields(),
    );

    if (nextPostsPagination) this.applyMorePosts(nextPostsPagination);
  }

  override connectedCallback() {
    super.connectedCallback();

    InstagramGalleryPagination.fetchInstagramGalleryPaginated('')(InstagramGalleryPagination.defaultFields())
      .then(nextPostsPagination => {
        if (nextPostsPagination) this.applyMorePosts(nextPostsPagination);
      })
      .catch(async err => {
        logger.warn({ err }, 'error while loading more posts');
        await XSnackbar.show('Leider konntent keine weiteren Posts geladen werden.');
      });
  }

  override render() {
    const displayModes = Object.values(InstagramFeedDisplayMode);

    return html`
      <d-flex space="md">
        <d-checkbox
          .label=${'Live Feed anzeigen'}
          .value=${this.content.value('countLiveFeedItems', this.size) !== null}
          @value-picked=${(e: ValueEvent<boolean>) => {
            this.content.setValue('countLiveFeedItems', e.detail.value ? 3 : null, this.size);
            this.host.requestUpdate();
          }}
        ></d-checkbox>

        <d-input placeholder="Seitenverhältnis" ${this.contentController.field('aspectRatio')}></d-input>
        <d-flex space="md">
          <d-dropdown-selection
            .value=${displayModes[0]}
            .selectables=${displayModes}
            ${this.contentController.field('displayMode')}
            @value-picked=${() => this.host.requestUpdate()}
          ></d-dropdown-selection>

          ${this.content.displayMode === InstagramFeedDisplayMode.GRID
            ? html`
                <d-input
                  type="number"
                  placeholder="Anzahl Posts pro Reihe"
                  ${this.contentController.field('lengthPerRow')}
                >
                </d-input>
              `
            : nothing}
          ${this.content.countLiveFeedItems === null
            ? html`
                <d-text> -Ausgewählte Posts- </d-text>
                <d-grid columns="25% 25% 25% 25%">
                  ${repeat(
                    this.content.chosenPosts,
                    curPost => curPost.postID,
                    curPost => this._renderPost(curPost, 'Aktiv'),
                  )}
                </d-grid>

                <d-hr></d-hr>

                <d-flex space="md">
                  <d-text> -Verfügbare Posts- </d-text>
                  <d-grid columns="33% 33% 33%">
                    ${repeat(
                      this._cachedPosts.value,
                      curPost => curPost.postID,
                      curPost => this._renderPost(curPost, 'Anzeigen'),
                    )}
                  </d-grid>

                  ${this._nextPagingUrl
                    ? html` <d-button @click=${() => this.loadMorePosts()}> Mehr Laden </d-button> `
                    : html`
                        <d-text ${css({ fontSize: this.spacing.sm, textAlign: 'center' })}>
                          Keine weiteren Posts verfügbar...
                        </d-text>
                      `}
                </d-flex>
              `
            : html`
                <d-input
                  type="number"
                  placeholder="Anzahl Posts"
                  ${this.contentController.field('countLiveFeedItems')}
                ></d-input>
              `}
        </d-flex>
      </d-flex>
    `;
  }

  private _renderPost(curPost: InstagramPost, checkBoxLabel: string) {
    const isSelected = !!this.content.chosenPosts.find(findPost => findPost.postID === curPost.postID);
    return html`
      <d-flex space="sm" padding="xs">
        <d-card
          space-between
          ${css({
            height: '100%',
            width: `CALC(100% - CALC(${this.spacing.sm} * 2))`,
            padding: this.spacing.sm,

            ...(isSelected
              ? {
                  background: this.colors.accent.shade(100),
                }
              : {}),
          })}
        >
          <img
            src="${curPost.thumbnailUrl || curPost.mediaUrl}"
            ${css({
              width: '100%',
              border: `1px solid ${this.colors.light.shade(600)}`,
              aspectRatio: '1 / 1',
              objectFit: 'cover',
            })}
          />
          <d-flex horizontal space="sm" space-between crossaxis-center>
            <d-text
              ${css({
                fontSize: '.875rem',
              })}
            >
              ${checkBoxLabel}:
            </d-text>
            <d-checkbox
              .value=${isSelected}
              @value-picked=${(checkEvt: ValueEvent<boolean>) => {
                checkEvt.detail.value
                  ? this.content.chosenPosts.push(curPost)
                  : (this.content.chosenPosts = this.content.chosenPosts.filter(
                      curChosenPost => curChosenPost.postID !== curPost.postID,
                    ));
              }}
            ></d-checkbox>
          </d-flex>
        </d-card>
      </d-flex>
    `;
  }
}

export const MarketingInstagramFeed: BuildifyExtension<BaseInstagramFeed> = {
  class: BaseInstagramFeed,
  toolbar: {
    icon: 'camera',
    text: {
      default: 'Instagram',
    },
  },
  group: 'Marketing',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-instagram-feed
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-instagram-feed>
    `;
  },
  editor: ({ contentController, controllerBaseKeyPath, content, host }) => {
    return html`
      <d-text tone="subtle"> Instagram-Feeds </d-text>
      <marketing-instagram-feed-editor
        .contentController=${contentController}
        .controllerBaseKeyPath=${controllerBaseKeyPath}
        .content=${content}
        .host=${host}
      ></marketing-instagram-feed-editor>
    `;
  },
};

@customElement('marketing-instagram-feed')
export class MInstagramFeed extends ExtensionBoilerplate<BaseInstagramFeed> {
  @state() private readonly _size = new WindowSizeController(this);

  @state() private readonly _liveFeed = new RXController(
    this,
    this.content.observable.pipe(
      map(content => this._convertedContent),
      map(content => content?.countLiveFeedItems),
      distinctUntilChanged(),
      switchMap(async countLiveFeedItems => {
        if (!countLiveFeedItems) return undefined;

        const posts = await InstagramGalleryPagination.fetchInstagramGalleryPaginated(
          '',
          countLiveFeedItems,
        )(InstagramGalleryPagination.defaultFields());
        return posts?.response;
      }),
    ),
  );

  override connectedCallback() {
    super.connectedCallback();

    consentCookieValueSubject.subscribe(value => {
      this.requestUpdate();
    });
  }

  override _content(content: BaseInstagramFeed) {
    const showPosts = this._liveFeed.value ?? content.chosenPosts;

    if (!CookieFactory.consentCookie.getValue('allowInstagram', 'boolean'))
      return html`
        <d-flex
          ${css({ width: '100%', aspectRatio: '16 / 9', color: '#fff', background: '#333' })}
          center
          crossaxis-center
          space="md"
        >
          <d-text> Bitte aktiviere Instagram-Cookies, um den Feed ansehen zu können. </d-text>
          <d-button
            @click=${() => {
              CookieFactory.consentCookie.setValue('allowInstagram', 'true');
              this.requestUpdate();
            }}
          >
            Cookies erlauben
          </d-button>
        </d-flex>
      `;

    if (showPosts.length === 0) return html` <d-text> Leider sind aktuell keine Instagram-Posts verfügbar! </d-text> `;

    return this._renderContainer(
      content,
      showPosts.map(curPost => {
        return html`
          <d-flex
            center
            crossaxis-center
            ${acss({
              borderRadius: '8px',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '4px 4px 40px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
              },
            })}
          >
            <d-flex
              center
              crossaxis-center
              flex
              w100
              ${acss({
                position: 'relative',
                background: this.colors.tone.whitePrimary,
                overflow: 'hidden',
                aspectRatio: content.aspectRatio ?? '1 / 1',

                '*': {
                  transition: 'all 150ms ease-in-out',
                },

                img: {
                  userSelect: 'none',
                  pointerEvents: 'none',
                  height: '100%',
                  width: '100%',
                  maxWidth: '100%',
                  objectFit: 'cover',

                  ...(content.displayMode === InstagramFeedDisplayMode.MOSAIK
                    ? {
                        maxHeight: '33vh',
                      }
                    : {}),
                },

                '&:hover': {
                  img: {
                    opacity: '.75',
                  },
                },
              })}
              @click=${() => window.open(curPost.postUrl, '_blank')}
            >
              <img
                alt="Instagram Beitrag: ${curPost.description}"
                src=${`${baseConfig.get('ROOT_URL')}api/instagram/${curPost.postID}`}
              />
            </d-flex>
          </d-flex>
        `;
      }),
    );
  }

  protected _renderContainer(content: BaseInstagramFeed, items: TemplateResult[]) {
    switch (content.displayMode) {
      case InstagramFeedDisplayMode.CAROUSEL:
        const visibleItems =
          { [Size.MOBILE]: 1, [Size.TABLET]: 2, [Size.DESKTOP]: 3 }[this._consumedSize.value ?? ''] ?? 3;
        return html`
          <component-carousel
            item-gap=${16}
            overflow="visible"
            duration="300"
            visible-items=${visibleItems}
            .items=${items.map(item => {
              return (isActive: boolean) => item;
            })}
          ></component-carousel>
        `;
      case InstagramFeedDisplayMode.MOSAIK:
        return html`<d-flex horizontal center wrap>${items}</d-flex>`;
      default:
        // grid view
        const itemsPerRow =
          content.lengthPerRow ??
          { [Size.MOBILE]: 1, [Size.TABLET]: 2, [Size.DESKTOP]: 3 }[this._consumedSize.value ?? ''] ??
          4;
        return html` <d-grid columns=${itemsPerRow} space="md">${items}</d-grid> `;
    }
  }
}
